import type { MarketPmfs, OddsRange, PropLine } from '@sharpr/pricing';
import { propLineFromRow, type PropLineRow, type StreamPosition } from '@sharpr/board';
import type { AccessTier, Book, BookId, HiddenBy, MarketCode, Sport } from '@sharpr/types';
import { firstBy, groupBy } from 'remeda';
import type { SportConfig, Workload } from './documents';
import type { GameId, PlayerId } from './ids';
import type { EffectiveMarketEntry, EffectivePlayer, EffectiveProjection, EffectiveProjections } from './effective';
import { parseScenarioKey, type ScenarioKey } from './scenario';

// The board's snapshot (spec §13.3): one per view (the cut the server served), built from the board's prop lines
// (@sharpr/board) and the effective projections. The board prices every line in the browser against the PMFs it carries;
// everything else it shows is here, once, at the level it belongs to.
export interface Seed {
  v: 1; sport: Sport;
  view: AccessTier;                          // the cut the server served (the client's tier is only a hint)
  // The board channel's stream position, read before the data, so a board resumes the stream from it (spec §13.4).
  // Absent when the server has no Centrifugo to ask.
  position?: StreamPosition;
  releaseTime?: string;                      // the sport's release time (the banner)
  oddsRange?: OddsRange;                     // the prices the board shows (absent: any)
  lastUpdated?: string;                      // latest projection stamp among the games this view sees
  sportVersion?: number;                     // the sport's board version its cuts were stored under (absent: none stored), to order per-game cuts
  configVersion?: number;                    // the sport version its release time and odds range were read at (absent: read live), to order the config
  books: Record<BookId, Book>;               // the catalog, by book id
  games: SeedGame[];                         // every game with lines
}

export type Side = 'home' | 'away';

export interface SeedGame {
  gameId: GameId; commenceTime: string;
  teams: Record<Side, SeedTeam>;
  players: SeedPlayer[];                     // the players with lines, and their lines
  projection?: SeedProjection;               // absent: this view sees no projection of the game (odds only)
  version?: number; sportVersion?: number;   // the versions of the cut it was joined with (absent: no cut), to order per-game cuts
}

export interface SeedTeam {
  abbr: string;
  note?: string;                             // the analyst's note on the team
}

export interface SeedPlayer {
  playerId: PlayerId; name: string; position?: string;
  side?: Side;                               // his team; the other is his opponent (absent: the worker could not place him)
  propLines: PropLine[];
  hiddenBy?: Partial<Record<MarketCode, HiddenBy[]>>;   // admin only: why subscribers don't see a market of his
  note?: string;                             // the analyst's note on him in this game
}

// The game's effective projection as this view sees it.
export interface SeedProjection {
  ruledOut: PlayerId[];                      // the players ruled out and live (sat by a stored scenario): the scenario with nothing marked
  questionable: Array<{ playerId: PlayerId; name: string; side: Side }>;   // the chips this view may mark
  scenarios: Partial<Record<ScenarioKey, Record<PlayerId, PlayerProjection>>>;   // every scenario this view may reach
}

export interface PlayerProjection { marketPmfs: MarketPmfs; workload: Workload }

export interface SeedSource {
  sport: Sport; position?: StreamPosition; rows: readonly PropLineRow[]; effective: EffectiveProjections;
  teamAbbrs: ReadonlyMap<string, string>; books: Book[];
  playerNames: ReadonlyMap<string, string>;   // questionable players' names (many have no prop lines)
}

// A view that sees projections: every view but the public one (odds only).
export type ProjectionView = Exclude<AccessTier, 'public'>;

// What a view sees of a player in one scenario: a subscriber the visible PMFs (an unreleased team's are not), an admin
// every PMF; none when the view sees no market of his. The one cut the board and its downloads apply.
export function viewProjection(view: ProjectionView, player: EffectivePlayer): PlayerProjection | undefined {
  const marketPmfs: MarketPmfs = {};
  for (const [market, m] of Object.entries(player.markets) as Array<[MarketCode, EffectiveMarketEntry]>) {
    if (view === 'admin' || m.visible) marketPmfs[market] = m.pmf;
  }
  return Object.keys(marketPmfs).length > 0 ? { marketPmfs, workload: player.workload } : undefined;
}

// Whether the analyst has released a team in this game; a team the game doesn't know, or no game, counts as unreleased.
function released(game: EffectiveProjection | undefined, teamId: string | null | undefined): boolean {
  return teamId != null && game?.teams.get(teamId)?.hidden === false;
}

// The open questionable players a view may mark out: an admin every one, anyone else only a released team's (a team
// the game doesn't know counts as unreleased). An unreleased team's open players are named nowhere outside admin, nor
// is any scenario sitting one of them.
export function markable(game: EffectiveProjection, view: ProjectionView): EffectiveProjection['questionable'] {
  return view === 'admin' ? game.questionable : game.questionable.filter(q => released(game, q.teamId));
}

// The game's projection as this view sees it, over every modeled player. A non-admin sees an unreleased team's numbers
// nowhere and its open questionable players nowhere: no chip, and no scenario sitting one of them. Chips only where there
// is a choice. A chip carries the registry's name when it has one (many questionable players have no lines to name them).
function viewGameProjection(view: ProjectionView, game: EffectiveProjection, secret: ReadonlySet<string>,
  sideOf: (teamId: string) => Side | undefined, names: ReadonlyMap<string, { name: string }>): CutProjection | undefined {
  const scenarios: SeedProjection['scenarios'] = {};
  for (const [scenario, inScenario] of game.scenarios) {
    if (parseScenarioKey(scenario).outPlayerIds.some(id => secret.has(id))) continue;
    const players: Record<PlayerId, PlayerProjection> = {};
    for (const [playerId, player] of inScenario) {
      const viewed = viewProjection(view, player);
      if (viewed) players[playerId] = viewed;
    }
    if (Object.keys(players).length > 0) scenarios[scenario as ScenarioKey] = players;
  }
  const keys = Object.keys(scenarios);
  if (keys.length === 0) return undefined;
  const questionable = keys.length < 2 ? [] : markable(game, view).flatMap(q => {
    const side = sideOf(q.teamId);
    if (!side) return [];
    const name = names.get(q.playerId)?.name;
    return [name ? { playerId: q.playerId, name, side } : { playerId: q.playerId, side }];
  });
  return { ruledOut: game.ruledOut, questionable, scenarios };
}

// One game as one view sees it, independent of its prop lines: the writer builds it once per view when the game changes
// and the board joins it with the lines it holds (withLinePlayers).
export interface GameCut {
  v: 1; sport: Sport; view: AccessTier; gameId: GameId;
  version: number; sportVersion: number;     // the game's board version and the sport's, as of the build
  commenceTime: string;
  teams: Record<Side, SeedTeam>;
  teamIds: Record<Side, string | null>;      // the schedule's team ids, to place a line's player by his team
  players: CutPlayer[];                      // every modeled player, and every player whose note this view reads (admin: every noted one)
  projection?: CutProjection;                // absent: this view sees no projection of the game (odds only)
  releaseTime?: string; oddsRange?: OddsRange;
  lastUpdated?: string;                      // the game's projection stamp, when this view sees its projection
}

// A chip without a name is a player the registry lacks: his lines name him, or there is no chip.
export interface CutProjection extends Omit<SeedProjection, 'questionable'> {
  questionable: Array<{ playerId: PlayerId; name?: string; side: Side }>;
}

export interface CutPlayer {
  playerId: PlayerId; name: string; position?: string; side?: Side;
  hiddenBy?: Partial<Record<MarketCode, HiddenBy[]>>;   // admin only: why subscribers don't see a market of his
  note?: string;
}

export interface GameCutSource {
  sport: Sport; game: EffectiveProjection; sportConfig: SportConfig; teamAbbrs: ReadonlyMap<string, string>;
  players: ReadonlyMap<PlayerId, { name: string; position?: string; teamId?: string }>;   // the registry's, by player id
  version: number; sportVersion: number;
}

// One game as this view sees it, lines aside. A player the registry cannot name is left out (never a raw id), as is,
// outside admin, an unreleased team's open questionable player and a player only a note this view does not read names.
export function buildGameCut(view: AccessTier, src: GameCutSource): GameCut {
  const { game } = src;
  const sideOf = (teamId: string | null | undefined): Side | undefined =>
    teamId == null ? undefined : teamId === game.home ? 'home' : teamId === game.away ? 'away' : undefined;
  const team = (teamId: string | null): SeedTeam => {
    const t: SeedTeam = { abbr: (teamId && src.teamAbbrs.get(teamId)) || '' };
    const state = teamId ? game.teams.get(teamId) : undefined;
    if (state?.note && (view === 'admin' || (view === 'subscriber' && released(game, teamId)))) t.note = state.note;
    return t;
  };
  const open = markable(game, view === 'public' ? 'subscriber' : view);
  const secret = new Set(game.questionable.filter(q => !open.includes(q)).map(q => q.playerId));
  const modeled = new Map<PlayerId, EffectivePlayer>();
  for (const inScenario of game.scenarios.values()) for (const [id, p] of inScenario) if (!modeled.has(id)) modeled.set(id, p);
  const ids = [...new Set([...modeled.keys(), ...game.playerNotes.keys()])].filter(id => view === 'admin' || !secret.has(id));
  const players = ids.flatMap((playerId): CutPlayer[] => {
    const known = src.players.get(playerId);
    if (!known) return [];
    const p: CutPlayer = { playerId, name: known.name };
    if (known.position) p.position = known.position;
    // His team in this game is the model's; the registry's can lag a trade.
    const teamId = modeled.get(playerId)?.teamId ?? known.teamId;
    const side = sideOf(teamId);
    if (side) p.side = side;
    if (view === 'admin') {
      // A player's hides are the same in every scenario: read them from any that holds him.
      const reasons = (Object.entries(modeled.get(playerId)?.markets ?? {}) as Array<[MarketCode, EffectiveMarketEntry]>).filter(([, m]) => m.hiddenBy.length > 0);
      if (reasons.length > 0) p.hiddenBy = Object.fromEntries(reasons.map(([m, e]) => [m, e.hiddenBy]));
    }
    // A player's note is served as his team's is; his market hides do not gate it.
    const note = game.playerNotes.get(playerId);
    if (note && (view === 'admin' || (view === 'subscriber' && side !== undefined && released(game, teamId)))) p.note = note;
    return modeled.has(playerId) || p.note ? [p] : [];
  });
  const cut: GameCut = {
    v: 1, sport: src.sport, view, gameId: game.gameId, version: src.version, sportVersion: src.sportVersion,
    commenceTime: game.commenceTime ?? '', teams: { home: team(game.home), away: team(game.away) },
    teamIds: { home: game.home, away: game.away }, players,
  };
  const { releaseTime, oddsRange } = src.sportConfig;
  if (releaseTime) cut.releaseTime = releaseTime;
  if (oddsRange.min !== null || oddsRange.max !== null) cut.oddsRange = oddsRange;
  if (view === 'public') return cut;
  const projection = viewGameProjection(view, game, secret, sideOf, src.players);
  if (projection) {
    cut.projection = projection;
    if (game.projectionsUpdatedAt) cut.lastUpdated = game.projectionsUpdatedAt;
  }
  return cut;
}

// A game's teams as its lines place them, for a game the schedule does not hold.
function lineTeams(rows: readonly PropLineRow[]): Record<Side, string | null> {
  return {
    home: rows.find(r => r.is_home === true)?.team_id ?? rows.find(r => r.is_home === false)?.opponent_team_id ?? null,
    away: rows.find(r => r.is_home === false)?.team_id ?? rows.find(r => r.is_home === true)?.opponent_team_id ?? null,
  };
}

// The scenarios' players narrowed to the ones with lines: a scenario left empty goes, and with fewer than two left there
// are no chips. A chip the registry could not name takes his lines' name, and without lines is no chip.
function withLineProjection(projection: CutProjection | undefined, lineNames: ReadonlyMap<string, string>): SeedProjection | undefined {
  if (!projection) return undefined;
  const scenarios: SeedProjection['scenarios'] = {};
  for (const [scenario, inScenario] of Object.entries(projection.scenarios) as Array<[ScenarioKey, Record<PlayerId, PlayerProjection>]>) {
    const players = Object.fromEntries(Object.entries(inScenario).filter(([id]) => lineNames.has(id)));
    if (Object.keys(players).length > 0) scenarios[scenario] = players;
  }
  const keys = Object.keys(scenarios);
  if (keys.length === 0) return undefined;
  const questionable = keys.length < 2 ? [] : projection.questionable.flatMap(q => {
    const name = q.name ?? lineNames.get(q.playerId);
    return name ? [{ playerId: q.playerId, name, side: q.side }] : [];
  });
  return { ruledOut: projection.ruledOut, questionable, scenarios };
}

// A player as his lines show him: named and placed by them.
export type LinePlayer = Pick<SeedPlayer, 'playerId' | 'name' | 'position' | 'side'>;
type CutFields = Pick<SeedPlayer, 'hiddenBy' | 'note'>;
export type CutGameOver<P extends LinePlayer> = Pick<SeedGame, 'commenceTime' | 'teams' | 'projection' | 'version' | 'sportVersion'> & {
  players: Array<Omit<P, keyof CutFields> & CutFields>;
};

// A game's cut over the players its lines show: each with what the cut carries of him (whatever an earlier cut carried
// goes), and the projection narrowed to them. A seed joins it with its lines (withLinePlayers); a board, with the players
// it holds. The one rule for which players a board shows.
export function withCut<P extends LinePlayer & CutFields>(cut: GameCut, linePlayers: readonly P[]): CutGameOver<P> {
  const cutPlayers = new Map(cut.players.map(p => [p.playerId, p]));
  const players = linePlayers.map(p => {
    const out: Omit<P, keyof CutFields> & CutFields = { ...p };
    delete out.hiddenBy;
    delete out.note;
    const fromCut = cutPlayers.get(p.playerId);
    if (fromCut?.hiddenBy) out.hiddenBy = fromCut.hiddenBy;
    // A player his lines cannot place counts as unreleased.
    if (fromCut?.note && (cut.view === 'admin' || p.side !== undefined)) out.note = fromCut.note;
    return out;
  });
  const game: CutGameOver<P> = { commenceTime: cut.commenceTime, teams: cut.teams, players, version: cut.version, sportVersion: cut.sportVersion };
  const projection = withLineProjection(cut.projection, new Map(players.map(p => [p.playerId, p.name])));
  if (projection) game.projection = projection;
  return game;
}

// A game as the board shows it: its cut (absent: a game nothing projects, odds only) joined with its lines. The board
// shows the players with lines, named and placed as their lines say (withCut).
export function withLinePlayers(gameId: GameId, cut: GameCut | undefined, rows: readonly PropLineRow[], teamAbbrs: ReadonlyMap<string, string>): SeedGame {
  const { home, away } = cut?.teamIds ?? lineTeams(rows);
  const sideOf = (teamId: string | null | undefined): Side | undefined =>
    teamId == null ? undefined : teamId === home ? 'home' : teamId === away ? 'away' : undefined;
  const players = Object.values(groupBy(rows, r => r.player_id)).map((own): SeedPlayer => {
    const r = own[0];
    const side = sideOf(r.team_id) ?? (r.team_id == null && r.is_home != null ? (r.is_home ? 'home' : 'away') : undefined);
    const p: SeedPlayer = { playerId: r.player_id, name: r.player_name, propLines: own.map(propLineFromRow) };
    if (r.position) p.position = r.position;
    if (side) p.side = side;
    return p;
  });
  if (cut) return { gameId, ...withCut(cut, players) };
  const teams = { home: { abbr: (home && teamAbbrs.get(home)) || '' }, away: { abbr: (away && teamAbbrs.get(away)) || '' } };
  return { gameId, commenceTime: rows[0].commence_time, teams, players };
}

// What a seed carries beside its games' cuts: the lines, the sport's config, the catalog, the stream position.
export interface SeedFrame {
  sport: Sport; position?: StreamPosition; rows: readonly PropLineRow[];
  teamAbbrs: ReadonlyMap<string, string>; books: Book[];
  sportConfig: Pick<SportConfig, 'releaseTime' | 'oddsRange'>;
  sportVersion?: number;                     // the stored cuts' manifest's
  configVersion?: number;                    // the sport version sportConfig was read at
}

// One view of the board from each game's cut (absent: odds only), joined with its lines (withLinePlayers). Pure: the
// server builds the cuts (buildSeed) or reads the stored ones.
export function assembleSeed(view: AccessTier, frame: SeedFrame, cutOf: (gameId: GameId, rows: readonly PropLineRow[]) => GameCut | undefined): Seed {
  const cuts: GameCut[] = [];
  const games = Object.entries(groupBy(frame.rows, r => r.game_id)).map(([gameId, rows]) => {
    const cut = cutOf(gameId, rows);
    if (cut) cuts.push(cut);
    return withLinePlayers(gameId, cut, rows, frame.teamAbbrs);
  });
  const { releaseTime, oddsRange } = frame.sportConfig;
  const seed: Seed = {
    v: 1, sport: frame.sport, view, position: frame.position,
    releaseTime: releaseTime ?? undefined,
    books: Object.fromEntries(frame.books.map(b => [b.id, b])), games,
  };
  if (oddsRange.min !== null || oddsRange.max !== null) seed.oddsRange = oddsRange;
  if (frame.sportVersion !== undefined) seed.sportVersion = frame.sportVersion;
  if (frame.configVersion !== undefined) seed.configVersion = frame.configVersion;
  if (view === 'public') return seed;
  // The latest projection stamp of the games this view sees, compared as time not text.
  const seen = new Set(games.filter(g => g.projection).map(g => g.gameId));
  const stamps = cuts.flatMap(c => (seen.has(c.gameId) && c.lastUpdated ? [c.lastUpdated] : []));
  const latest = firstBy(stamps, [t => Date.parse(t), 'desc']);
  if (latest) seed.lastUpdated = latest;
  return seed;
}

// One view of the board, its cuts built from the effective projections. Pure: the caller reads the sources once and
// builds every view from them.
export function buildSeed(view: AccessTier, src: SeedSource): Seed {
  const { sportConfig } = src.effective;
  const seed = assembleSeed(view, { ...src, sportConfig }, (gameId, rows) => {
    const scheduled = src.effective.byGame.get(gameId);
    if (!scheduled) return undefined;
    // A game the schedule lacks takes its teams as its lines place them, and its tip-off from the row it was written under.
    const fromLines = lineTeams(rows);
    const game = { ...scheduled, home: scheduled.home ?? fromLines.home, away: scheduled.away ?? fromLines.away,
      commenceTime: scheduled.commenceTime ?? rows[0].commence_time };
    const players = new Map<PlayerId, { name: string; position?: string; teamId?: string }>();
    for (const r of rows) {
      if (players.has(r.player_id)) continue;
      const teamId = r.team_id ?? (r.is_home == null ? undefined : (r.is_home ? game.home : game.away) ?? undefined);
      players.set(r.player_id, { name: src.playerNames.get(r.player_id) ?? r.player_name, teamId });
    }
    for (const [id, name] of src.playerNames) if (!players.has(id)) players.set(id, { name });
    return buildGameCut(view, { sport: src.sport, game, sportConfig, teamAbbrs: src.teamAbbrs, players, version: 0, sportVersion: 0 });
  });
  // Cuts built here were never stored: they have no versions to order by.
  for (const g of seed.games) { delete g.version; delete g.sportVersion; }
  return seed;
}
